import { useState, useRef, useCallback } from "react";
import { RefreshCw, ArrowDown, Check } from "lucide-react";
import { haptic } from "@/lib/haptic";
import { useI18n } from "@/lib/i18n";

const THRESHOLD = 80;

export default function PullToRefresh({ onRefresh, children, disabled }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshDone, setRefreshDone] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);
  const thresholdReached = useRef(false);
  const containerRef = useRef(null);
  const { t } = useI18n();

  const onTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    const container = containerRef.current;
    if (!container || container.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
    pulling.current = true;
    thresholdReached.current = false;
    setRefreshDone(false);
  }, [disabled, isRefreshing]);

  const onTouchMove = useCallback((e) => {
    if (!pulling.current || isRefreshing) return;
    const delta = e.touches[0].clientY - startY.current;
    if (delta > 0) {
      const distance = Math.min(delta * 0.4, 120);
      setPullDistance(distance);
      if (distance >= THRESHOLD && !thresholdReached.current) {
        thresholdReached.current = true;
        haptic("light");
      }
    }
  }, [isRefreshing]);

  const onTouchEnd = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    if (pullDistance >= THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      haptic("medium");
      try {
        await onRefresh();
      } catch (e) {
        // ignore
      }
      setRefreshDone(true);
      setIsRefreshing(false);
      setTimeout(() => {
        setPullDistance(0);
        setRefreshDone(false);
      }, 600);
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, isRefreshing, onRefresh]);

  const progress = Math.min(pullDistance / THRESHOLD, 1);
  const rotation = progress * 360;
  const reachedThreshold = pullDistance >= THRESHOLD;

  return (
    <div
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      className="relative"
    >
      {/* Pull indicator */}
      <div
        className="absolute left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 transition-opacity duration-200"
        style={{
          top: Math.max(pullDistance - 50, -50),
          opacity: progress > 0.1 ? progress : 0,
        }}
      >
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
            refreshDone
              ? "bg-green-500/20 border-2 border-green-500/50 scale-110"
              : isRefreshing
              ? "bg-cyan-500/20 border-2 border-cyan-500/50"
              : reachedThreshold
              ? "bg-cyan-500/20 border-2 border-cyan-500/50 scale-110"
              : "bg-zinc-900 border border-zinc-700"
          }`}
        >
          {refreshDone ? (
            <Check className="w-4 h-4 text-green-400 animate-scale-in" />
          ) : isRefreshing ? (
            <RefreshCw className="w-4 h-4 text-cyan-400 animate-spin" />
          ) : reachedThreshold ? (
            <RefreshCw
              className="w-4 h-4 text-cyan-400"
              style={{ transform: `rotate(${rotation}deg)` }}
            />
          ) : (
            <ArrowDown
              className="w-4 h-4 text-zinc-400 transition-transform duration-200"
              style={{ transform: `rotate(${progress * 180}deg)` }}
            />
          )}
        </div>

        {/* Status text */}
        <span className={`font-mono text-[10px] transition-colors duration-200 ${
          refreshDone ? "text-green-400" : isRefreshing ? "text-cyan-400" : reachedThreshold ? "text-cyan-400" : "text-zinc-600"
        }`}>
          {refreshDone ? t.pullDone : isRefreshing ? t.pullRefreshing : reachedThreshold ? t.pullRelease : t.pullToRefresh}
        </span>

        {/* Progress bar */}
        {!isRefreshing && !refreshDone && (
          <div className="w-16 h-0.5 bg-zinc-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-colors duration-100 ${
                reachedThreshold ? "bg-cyan-400" : "bg-zinc-600"
              }`}
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}
      </div>

      {/* Content with pull offset */}
      <div
        style={{
          transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : undefined,
          transition: pulling.current ? "none" : "transform 0.3s ease-out",
        }}
      >
        {children}
      </div>
    </div>
  );
}
